import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Phone, PhoneOff, Mic, MicOff, Volume2, Clock, Signal, PhoneCall, X } from 'lucide-react';
import { useTwilioContext } from '@/context/TwilioContext';

interface CallStatusCardProps {
  onEndCall: () => void;
  onMuteToggle: () => void;
}

export const CallStatusCard = ({ 
  onEndCall, 
  onMuteToggle
}: CallStatusCardProps) => {
  // Local speaker state for this component only
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  
  const { 
    callInProgress, 
    callStatus, 
    formattedDuration, 
    isMuted,
    dismissCall
  } = useTwilioContext();

  console.log('🎯 CallStatusCard: Using global context state:', {
    callInProgress,
    callStatus,
    formattedDuration,
    isMuted
  });

  const handleSpeakerToggle = () => {
    console.log('🔊 CallStatusCard: Toggling speaker from', isSpeakerOn, 'to', !isSpeakerOn);
    setIsSpeakerOn(!isSpeakerOn);
  };

  const isCallEnded = callStatus === 'ended';
  const hasCallData = callStatus !== 'idle';

  if (!hasCallData) {
    return (
      <Card className="w-full max-w-md mx-auto shadow-sm">
        <CardContent className="p-4 md:p-6 text-center">
          <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3 md:mb-4">
            <Phone className="h-6 w-6 md:h-8 md:w-8 text-primary" />
          </div>
          <h3 className="text-base md:text-lg font-semibold mb-2">Ready to Call</h3>
          <p className="text-xs md:text-sm text-muted-foreground">
            Use the dialer to make a call
          </p>
        </CardContent>
      </Card>
    );
  }

  const getStatusColor = () => {
    if (isCallEnded) return 'bg-muted-foreground';
    switch (callStatus) {
      case 'ringing':
        return 'bg-blue-500';
      case 'connecting':
        return 'bg-yellow-500';
      case 'open':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusText = () => {
    if (isCallEnded) return 'Call Ended';
    switch (callStatus) {
      case 'ringing':
        return 'Ringing';
      case 'connecting':
        return 'Connecting';
      case 'open':
        return 'Connected';
      case 'idle':
        return 'Ready';
      default:
        return callStatus.charAt(0).toUpperCase() + callStatus.slice(1);
    }
  };

  const getStatusBadgeVariant = () => {
    if (isCallEnded) return 'outline' as const;
    switch (callStatus) {
      case 'ringing':
        return 'secondary' as const;
      case 'connecting':
        return 'outline' as const;
      case 'open':
        return 'default' as const;
      default:
        return 'outline' as const;
    }
  };

  const getStatusBadgeText = () => {
    if (isCallEnded) return 'Ended';
    switch (callStatus) {
      case 'ringing':
        return 'Ringing';
      case 'connecting':
        return 'Connecting';
      case 'open':
        return 'Live';
      default:
        return callStatus;
    }
  };

  // Call ended state
  if (isCallEnded) {
    return (
      <Card className="w-full border-0 shadow-none bg-transparent">
        <CardContent className="p-0">
          {/* Mobile Layout */}
          <div className="block md:hidden">
            <div className="text-center space-y-3">
              <div className={`w-16 h-16 rounded-full ${getStatusColor()} flex items-center justify-center mx-auto`}>
                <PhoneOff className="h-8 w-8 text-white" />
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-center gap-2">
                  <span className="font-semibold text-lg">{getStatusText()}</span>
                  <Badge variant={getStatusBadgeVariant()} className="text-xs">{getStatusBadgeText()}</Badge>
                </div>
                {formattedDuration && formattedDuration !== '00:00' && (
                  <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    <span className="font-mono text-base">{formattedDuration}</span>
                  </div>
                )}
              </div>
              <div className="pt-2">
                <Button variant="outline" size="sm" onClick={dismissCall} className="gap-1">
                  <X className="h-3 w-3" />
                  Close
                </Button>
              </div>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden md:flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full ${getStatusColor()} flex items-center justify-center`}>
                <PhoneOff className="h-5 w-5 text-white" />
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{getStatusText()}</span>
                  <Badge variant={getStatusBadgeVariant()} className="text-xs">{getStatusBadgeText()}</Badge>
                </div>
                {formattedDuration && formattedDuration !== '00:00' && (
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    <span className="font-mono">{formattedDuration}</span>
                  </div>
                )}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={dismissCall} className="gap-1">
              <X className="h-3 w-3" />
              Close
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Active call (mobile + desktop layouts unchanged)
  return (
    <Card className="w-full border-0 shadow-none bg-transparent">
      <CardContent className="p-0">
        {/* Mobile Layout */}
        <div className="block md:hidden">
          <div className="text-center space-y-3">
            <div className={`w-16 h-16 rounded-full ${getStatusColor()} flex items-center justify-center mx-auto ${callStatus === 'ringing' ? 'animate-pulse' : ''}`}>
              <PhoneCall className="h-8 w-8 text-white" />
            </div>
            
            <div className="space-y-1">
              <div className="flex items-center justify-center gap-2">
                <span className="font-semibold text-lg">{getStatusText()}</span>
                <Badge variant={getStatusBadgeVariant()} className="text-xs">
                  {getStatusBadgeText()}
                </Badge>
              </div>
              
              {formattedDuration && callStatus === 'open' && (
                <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  <span className="font-mono text-base">{formattedDuration}</span>
                </div>
              )}
            </div>

            {/* Mobile Call Controls */}
            <div className="flex justify-center gap-3 pt-2">
              <Button
                variant={isMuted ? "destructive" : "outline"}
                size="lg"
                onClick={onMuteToggle}
                className="rounded-full w-12 h-12 p-0"
              >
                {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
              </Button>

              <Button
                variant={isSpeakerOn ? "default" : "outline"}
                size="lg"
                onClick={handleSpeakerToggle}
                className="rounded-full w-12 h-12 p-0"
              >
                <Volume2 className="h-5 w-5" />
              </Button>

              <Button
                variant="destructive"
                size="lg"
                onClick={onEndCall}
                className="rounded-full w-14 h-12 p-0"
              >
                <PhoneOff className="h-5 w-5" />
              </Button>
            </div>

            {/* Mobile Status Indicators */}
            <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground pt-2">
              <div className="flex items-center gap-1">
                <Signal className="h-3 w-3 text-green-500" />
                <span>Good</span>
              </div>
              
              {isMuted && (
                <div className="flex items-center gap-1 text-red-500">
                  <MicOff className="h-3 w-3" />
                  <span>Muted</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:flex items-center justify-between">
          {/* Left: Call Info */}
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full ${getStatusColor()} flex items-center justify-center ${callStatus === 'ringing' ? 'animate-pulse' : ''}`}>
              <Phone className="h-5 w-5 text-white" />
            </div>
            
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <span className="font-medium">{getStatusText()}</span>
                <Badge variant={getStatusBadgeVariant()} className="text-xs">
                  {getStatusBadgeText()}
                </Badge>
              </div>
              
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                {formattedDuration && callStatus === 'open' && (
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span className="font-mono">{formattedDuration}</span>
                  </div>
                )}
                
                <div className="flex items-center gap-1">
                  <Signal className="h-3 w-3 text-green-500" />
                  <span>Good</span>
                </div>
                
                {isMuted && (
                  <div className="flex items-center gap-1 text-red-500">
                    <MicOff className="h-3 w-3" />
                    <span>Muted</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: Call Controls */}
          <div className="flex items-center gap-2">
            <Button
              variant={isMuted ? "destructive" : "outline"}
              size="sm"
              onClick={onMuteToggle}
              className="rounded-full w-10 h-10 p-0"
            >
              {isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </Button>

            <Button
              variant={isSpeakerOn ? "default" : "outline"}
              size="sm"
              onClick={handleSpeakerToggle}
              className="rounded-full w-10 h-10 p-0"
            >
              <Volume2 className="h-4 w-4" />
            </Button>

            <Button
              variant="destructive"
              size="sm"
              onClick={onEndCall}
              className="rounded-full w-12 h-10 p-0"
            >
              <PhoneOff className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
